// Data behind the driver dashboard's Notifications tab: open shift count and
// this week's open (not-yet-completed) jobs, scoped to one driver. The open-
// jobs query mirrors ClosedCheck.tsx's admin-wide version (same
// taxicaller_data / driver_completed IS NULL / EXCLUDED_FLEET shape), just
// narrowed to a single driver and always "this week" rather than an
// admin-picked date range.
import { supabase } from "@/lib/supabase";

const EXCLUDED_FLEET = "stss";

const startOfWeekMon = (d: Date) => {
  const x = new Date(d);
  const dow = x.getDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
};

const endOfWeekMonToSun = (d: Date) => {
  const s = startOfWeekMon(d);
  const e = new Date(s);
  e.setDate(e.getDate() + 6);
  e.setHours(23, 59, 59, 999);
  return e;
};

const fmtIsoLocal = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** Shifts this driver has started but not closed off (shift_status = 'active'). */
export async function getOpenShiftCount(driverName: string): Promise<number> {
  if (!driverName) return 0;
  const { count, error } = await supabase
    .from("driver_shifts")
    .select("id", { count: "exact", head: true })
    .eq("driver_name", driverName)
    .eq("shift_status", "active");
  if (error) {
    console.error("Error fetching open shift count:", error);
    return 0;
  }
  return count ?? 0;
}

/**
 * This driver's not-yet-completed jobs, ANY date -- the same
 * driver_completed is null/false + not Cancelled/No show filter
 * JobUploadSection.tsx ("Uncomplete Jobs" tab) uses, so the tab badge
 * always matches what that tab actually lists. Deliberately not scoped to
 * "this week" (unlike getOpenJobsThisWeekCount below) since the tab itself
 * isn't either.
 */
export async function getOpenJobsCount(driverName: string): Promise<number> {
  if (!driverName) return 0;
  const { data, error } = await supabase
    .from("taxicaller_data")
    .select("Job_ID, Stage")
    .ilike("driver_name", driverName.trim())
    .or("driver_completed.is.null,driver_completed.eq.false");

  if (error) {
    console.error("Error fetching open jobs count:", error);
    return 0;
  }
  return (data || []).filter((j) => j.Stage !== "Cancelled" && j.Stage !== "No show").length;
}

/** This driver's not-yet-completed jobs for the current Mon-Sun week -- same definition ClosedCheck.tsx's admin "Open Jobs" tile uses. */
export async function getOpenJobsThisWeekCount(driverName: string): Promise<number> {
  if (!driverName) return 0;
  const startIso = fmtIsoLocal(startOfWeekMon(new Date()));
  const endIso = fmtIsoLocal(endOfWeekMonToSun(new Date()));

  const { data, error } = await supabase
    .from("taxicaller_data")
    .select("Job_ID, Stage")
    .eq("driver_name", driverName)
    .gte("pu_date", startIso)
    .lte("pu_date", endIso)
    .is("driver_completed", null)
    .neq("fleet", EXCLUDED_FLEET);

  if (error) {
    console.error("Error fetching open jobs count:", error);
    return 0;
  }
  // Cancelled / no-show jobs never get closed out with a payment, so they
  // aren't "open" -- same rule as getOpenJobsCount above and ClosedCheck.
  return (data || []).filter((j) => j.Stage !== "Cancelled" && j.Stage !== "No show").length;
}
